import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft, FileText, Scale } from 'lucide-react';
import { Button } from '@/components/ui/button';

const TERMS_CONTENT = {
  fr: {
    title: "Conditions d'utilisation",
    lastUpdated: "Dernière mise à jour : 24 février 2026",
    backToLogin: "Retour à la connexion",
    sections: [
      {
        title: "1. Acceptation des conditions",
        content: `En accédant et en utilisant SpaceLeadX ("le Service"), vous acceptez d'être lié par ces Conditions d'utilisation. Si vous n'acceptez pas ces conditions, veuillez ne pas utiliser le Service.

SpaceLeadX est un service fourni par SpaceLeadX Inc., une société constituée au Canada.`
      },
      {
        title: "2. Description du service",
        content: `SpaceLeadX est une plateforme SaaS (Software as a Service) de gestion de prospects conçue pour les entrepreneurs locaux. Le Service comprend :

• Création et gestion de formulaires de capture de prospects
• Tableau de bord de gestion des prospects avec pipeline Kanban
• Création de pages de destination optimisées pour le SEO
• Outils de suivi et d'analyse
• Intégrations avec des services tiers`
      },
      {
        title: "3. Inscription et compte",
        content: `Pour utiliser le Service, vous devez créer un compte. Vous acceptez de :

• Fournir des informations exactes, actuelles et complètes
• Maintenir et mettre à jour vos informations
• Protéger la confidentialité de votre mot de passe
• Être responsable de toute activité sur votre compte
• Nous informer immédiatement de toute utilisation non autorisée

Vous devez avoir au moins 18 ans ou l'âge légal de la majorité dans votre juridiction pour utiliser le Service.`
      },
      {
        title: "4. Utilisation acceptable",
        content: `Vous acceptez de ne pas utiliser le Service pour :

• Violer des lois ou règlements applicables
• Envoyer des communications non sollicitées (spam)
• Collecter des données personnelles sans consentement
• Transmettre des virus ou codes malveillants
• Tenter d'accéder à des systèmes non autorisés
• Usurper l'identité d'une autre personne ou entité
• Violer les droits de propriété intellectuelle

Nous nous réservons le droit de suspendre ou de résilier votre compte en cas de violation de ces conditions.`
      },
      {
        title: "5. Propriété intellectuelle",
        content: `Le Service et tout son contenu, fonctionnalités et caractéristiques sont la propriété de SpaceLeadX Inc. et sont protégés par les lois canadiennes et internationales sur le droit d'auteur, les marques de commerce et autres droits de propriété intellectuelle.

Vous conservez tous les droits sur les données que vous téléchargez ou créez via le Service. En utilisant le Service, vous nous accordez une licence limitée pour traiter vos données aux fins de fournir le Service.`
      },
      {
        title: "6. Paiement et facturation",
        content: `Certaines fonctionnalités du Service peuvent nécessiter un abonnement payant. En souscrivant :

• Vous autorisez les frais récurrents selon le plan choisi
• Les prix peuvent être modifiés avec un préavis de 30 jours
• Les remboursements sont accordés selon notre politique de remboursement
• La non-utilisation ne donne pas droit à un remboursement

Les taxes applicables seront ajoutées au prix indiqué selon votre juridiction.`
      },
      {
        title: "7. Confidentialité et données",
        content: `Notre utilisation de vos données personnelles est régie par notre Politique de confidentialité. En utilisant le Service, vous consentez à la collecte et à l'utilisation de vos données conformément à cette politique.

Nous nous conformons aux lois canadiennes sur la protection des données, y compris la LPRPDE (Loi sur la protection des renseignements personnels et les documents électroniques).`
      },
      {
        title: "8. Limitation de responsabilité",
        content: `DANS LA MESURE PERMISE PAR LA LOI :

• Le Service est fourni "tel quel" sans garantie d'aucune sorte
• Nous ne garantissons pas que le Service sera ininterrompu ou sans erreur
• Nous ne sommes pas responsables des dommages indirects, accessoires ou consécutifs
• Notre responsabilité totale est limitée aux frais payés au cours des 12 derniers mois

Certaines juridictions n'autorisent pas l'exclusion de certaines garanties, ces limitations peuvent ne pas s'appliquer à vous.`
      },
      {
        title: "9. Indemnisation",
        content: `Vous acceptez d'indemniser et de dégager de toute responsabilité SpaceLeadX Inc., ses dirigeants, administrateurs, employés et agents contre toute réclamation, dommage, perte ou dépense découlant de :

• Votre utilisation du Service
• Votre violation de ces Conditions
• Votre violation des droits de tiers`
      },
      {
        title: "10. Résiliation",
        content: `Vous pouvez résilier votre compte à tout moment via les paramètres du compte. Nous pouvons suspendre ou résilier votre accès :

• Pour violation de ces Conditions
• Pour protéger le Service ou d'autres utilisateurs
• Si requis par la loi

À la résiliation, votre droit d'utiliser le Service cesse immédiatement. Les dispositions qui devraient survivre à la résiliation resteront en vigueur.`
      },
      {
        title: "11. Modifications des conditions",
        content: `Nous pouvons modifier ces Conditions à tout moment. Les modifications importantes seront notifiées par :

• Affichage sur le Service
• Notification par courriel

Votre utilisation continue du Service après la notification constitue votre acceptation des nouvelles conditions.`
      },
      {
        title: "12. Loi applicable et juridiction",
        content: `Ces Conditions sont régies par les lois de la province de Québec et les lois fédérales du Canada applicables. Tout litige sera soumis à la compétence exclusive des tribunaux de Montréal, Québec.`
      },
      {
        title: "13. Contact",
        content: `Pour toute question concernant ces Conditions d'utilisation, veuillez nous contacter :

SpaceLeadX Inc.
Courriel : legal@spaceleadx.ai
Adresse : Montréal, Québec, Canada`
      }
    ]
  },
  en: {
    title: "Terms of Use",
    lastUpdated: "Last updated: February 24, 2026",
    backToLogin: "Back to login",
    sections: [
      {
        title: "1. Acceptance of Terms",
        content: `By accessing and using SpaceLeadX ("the Service"), you agree to be bound by these Terms of Use. If you do not accept these terms, please do not use the Service.

SpaceLeadX is a service provided by SpaceLeadX Inc., a company incorporated in Canada.`
      },
      {
        title: "2. Description of Service",
        content: `SpaceLeadX is a SaaS (Software as a Service) lead management platform designed for local entrepreneurs. The Service includes:

• Lead capture form creation and management
• Lead management dashboard with Kanban pipeline
• SEO-optimized landing page creation
• Tracking and analytics tools
• Third-party service integrations`
      },
      {
        title: "3. Registration and Account",
        content: `To use the Service, you must create an account. You agree to:

• Provide accurate, current, and complete information
• Maintain and update your information
• Protect the confidentiality of your password
• Be responsible for all activity on your account
• Notify us immediately of any unauthorized use

You must be at least 18 years old or the legal age of majority in your jurisdiction to use the Service.`
      },
      {
        title: "4. Acceptable Use",
        content: `You agree not to use the Service to:

• Violate any applicable laws or regulations
• Send unsolicited communications (spam)
• Collect personal data without consent
• Transmit viruses or malicious code
• Attempt to access unauthorized systems
• Impersonate another person or entity
• Violate intellectual property rights

We reserve the right to suspend or terminate your account for violation of these terms.`
      },
      {
        title: "5. Intellectual Property",
        content: `The Service and all its content, features, and functionality are owned by SpaceLeadX Inc. and are protected by Canadian and international copyright, trademark, and other intellectual property laws.

You retain all rights to data you upload or create through the Service. By using the Service, you grant us a limited license to process your data for purposes of providing the Service.`
      },
      {
        title: "6. Payment and Billing",
        content: `Some features of the Service may require a paid subscription. By subscribing:

• You authorize recurring charges according to the chosen plan
• Prices may be changed with 30 days notice
• Refunds are granted according to our refund policy
• Non-use does not entitle you to a refund

Applicable taxes will be added to the stated price based on your jurisdiction.`
      },
      {
        title: "7. Privacy and Data",
        content: `Our use of your personal data is governed by our Privacy Policy. By using the Service, you consent to the collection and use of your data in accordance with that policy.

We comply with Canadian data protection laws, including PIPEDA (Personal Information Protection and Electronic Documents Act).`
      },
      {
        title: "8. Limitation of Liability",
        content: `TO THE EXTENT PERMITTED BY LAW:

• The Service is provided "as is" without warranty of any kind
• We do not guarantee the Service will be uninterrupted or error-free
• We are not liable for indirect, incidental, or consequential damages
• Our total liability is limited to fees paid in the last 12 months

Some jurisdictions do not allow the exclusion of certain warranties, so these limitations may not apply to you.`
      },
      {
        title: "9. Indemnification",
        content: `You agree to indemnify and hold harmless SpaceLeadX Inc., its officers, directors, employees, and agents from any claims, damages, losses, or expenses arising from:

• Your use of the Service
• Your violation of these Terms
• Your violation of third-party rights`
      },
      {
        title: "10. Termination",
        content: `You may terminate your account at any time through account settings. We may suspend or terminate your access:

• For violation of these Terms
• To protect the Service or other users
• If required by law

Upon termination, your right to use the Service ceases immediately. Provisions that should survive termination will remain in effect.`
      },
      {
        title: "11. Changes to Terms",
        content: `We may modify these Terms at any time. Material changes will be notified by:

• Posting on the Service
• Email notification

Your continued use of the Service after notification constitutes acceptance of the new terms.`
      },
      {
        title: "12. Governing Law and Jurisdiction",
        content: `These Terms are governed by the laws of the Province of Quebec and the federal laws of Canada applicable therein. Any dispute shall be subject to the exclusive jurisdiction of the courts of Montreal, Quebec.`
      },
      {
        title: "13. Contact",
        content: `For any questions regarding these Terms of Use, please contact us:

SpaceLeadX Inc.
Email: legal@spaceleadx.ai
Address: Montreal, Quebec, Canada`
      }
    ]
  }
};

export function TermsPage() {
  const location = useLocation();
  const lang = location.pathname.startsWith('/fr') ? 'fr' : 'en';
  const content = TERMS_CONTENT[lang] || TERMS_CONTENT.en;
  const otherLang = lang === 'fr' ? 'en' : 'fr';
  const otherPath = lang === 'fr' ? '/en/terms-of-use' : '/fr/conditions-dutilisation';

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary to-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b border-border">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/login" className="flex items-center gap-2">
            <span className="font-heading text-xl font-black tracking-tight">
              Space<span className="text-primary">LeadX</span>
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <Link 
              to={otherPath}
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              {otherLang.toUpperCase()}
            </Link>
            <Button variant="outline" size="sm" asChild>
              <Link to="/login">
                <ArrowLeft className="h-4 w-4 mr-2" />
                {content.backToLogin}
              </Link>
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-card rounded-xl shadow-lg border border-border p-8 md:p-12">
          {/* Title */}
          <div className="flex items-center gap-4 mb-8">
            <div className="p-3 bg-primary/10 rounded-lg">
              <Scale className="h-8 w-8 text-primary" />
            </div>
            <div>
              <h1 className="font-heading text-3xl font-black tracking-tight">
                {content.title}
              </h1>
              <p className="text-muted-foreground mt-1">
                {content.lastUpdated}
              </p>
            </div>
          </div>

          {/* Sections */}
          <div className="prose prose-slate max-w-none">
            {content.sections.map((section, index) => (
              <section key={index} className="mb-8">
                <h2 className="text-xl font-bold text-foreground mb-4">
                  {section.title}
                </h2>
                <div className="text-muted-foreground whitespace-pre-line leading-relaxed">
                  {section.content}
                </div>
              </section>
            ))}
          </div>
        </div>

        {/* Footer */}
        <footer className="mt-8 text-center text-sm text-muted-foreground">
          <p>© 2026 SpaceLeadX Inc. {lang === 'fr' ? 'Tous droits réservés.' : 'All rights reserved.'}</p>
          <div className="mt-2 flex justify-center gap-4">
            <Link 
              to={lang === 'fr' ? '/fr/politique-de-confidentialite' : '/en/privacy-policy'}
              className="hover:text-primary transition-colors"
            >
              {lang === 'fr' ? 'Politique de confidentialité' : 'Privacy Policy'}
            </Link>
          </div>
        </footer>
      </main>
    </div>
  );
}
